/**
 * Optional second microphone capture, opened without echo cancellation, noise
 * suppression or gain control, used only for measurement.
 *
 * The processed stream that goes to OpenAI is the one that solved the fan/traffic
 * and echo problems, so it stays exactly as it is. But that same processing is
 * hostile to proximity measurement: the suppressor reshapes the spectrum (which
 * corrupts spectral tilt) and gates offsets (which flattens the decay slope), and
 * AGC compresses the dynamics that modulation depth is made of. Measuring an
 * unprocessed copy gives clean features without touching the uplink.
 *
 * Off by default. Two concurrent captures of one device are not universally
 * supported, and on some platforms opening a second one with conflicting audio
 * processing can disturb the first — which would regress working echo
 * cancellation. Enable deliberately, per browser, and check the reported source:
 *
 *   localStorage.setItem('voiceRawAnalysis', '1')
 */

const FLAG_KEY = 'voiceRawAnalysis';

export const RAW_ANALYSIS_CONSTRAINTS = {
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
};

export function rawAnalysisEnabled() {
  try {
    return window.localStorage.getItem(FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Whether the browser honoured the request. Settings are reported inconsistently,
 * so an unreported flag counts as unknown rather than as a failure.
 */
const describeProcessing = (settings) => {
  const keys = ['echoCancellation', 'noiseSuppression', 'autoGainControl'];
  const reported = keys.filter((key) => typeof settings[key] === 'boolean');
  if (!reported.length) return 'unknown';
  return reported.every((key) => settings[key] === false) ? 'unprocessed' : 'processed';
};

/**
 * Resolves to the stream that should be analysed. Always succeeds: any failure
 * falls back to the processed stream so metering keeps working.
 */
export async function openAnalysisStream(processedStream) {
  const fallback = { stream: processedStream, source: 'processed', owned: false };
  if (!rawAnalysisEnabled()) return fallback;
  if (!navigator?.mediaDevices?.getUserMedia) return fallback;

  const deviceId = processedStream?.getAudioTracks?.()[0]?.getSettings?.().deviceId;
  const audio = deviceId
    ? { ...RAW_ANALYSIS_CONSTRAINTS, deviceId: { exact: deviceId } }
    : { ...RAW_ANALYSIS_CONSTRAINTS };

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio });
    const settings = stream.getAudioTracks()[0]?.getSettings?.() || {};
    return {
      stream,
      source: 'raw',
      owned: true,
      processing: describeProcessing(settings),
      sampleRate: settings.sampleRate,
    };
  } catch (error) {
    return { ...fallback, error: error?.name || String(error) };
  }
}

/** Stops a stream this module opened. Never stops the caller's uplink stream. */
export function closeAnalysisStream(handle) {
  if (!handle?.owned || !handle.stream) return;
  try {
    handle.stream.getTracks().forEach((track) => track.stop());
  } catch {
    /* already gone */
  }
}
